import { z } from 'zod';
import { ProductSchema } from './ProductSchema';
import { BaseFilterSchema } from './FilterSchema';
// SortOption schema
export const SortOptionSchema = z.object({
    id: z.string(),
    label: z.string(),
});
// Suggestion schema with enhanced fields
export const SuggestionSchema = z.object({
    text: z.string(),
    type: z.enum(['query', 'category', 'product', 'collection', 'page']),
    // Additional data for rich suggestions
    title: z.string().optional(),
    handle: z.string().optional(),
    image: z.string().optional(),
    price: z.object({
        regular: z.number(),
        sale: z.number().optional(),
    }).optional(),
    url: z.string().optional(),
});
// SearchAnalytics schema
export const SearchAnalyticsSchema = z.object({
    queryID: z.string().optional(),
    searchTermID: z.string().optional(),
    sessionID: z.string().optional(),
    userToken: z.string().optional(),
    abTestInfo: z.object({
        testID: z.union([z.string(), z.number()]),
        variantID: z.union([z.string(), z.number()]),
        experimentID: z.string().optional(), // Unbxd specific
    }).optional(),
    performance: z.object({
        queryTime: z.number(),
        totalTime: z.number().optional(),
        fromCache: z.boolean().optional(),
    }).optional(),
    context: z.object({
        device: z.enum(['mobile', 'tablet', 'desktop']).optional(),
        location: z.string().optional(),
        referrer: z.string().optional(),
        timestamp: z.number().optional(),
    }).optional(),
});
// MerchandisingApplied schema
export const MerchandisingAppliedSchema = z.object({
    rules: z.array(z.object({
        id: z.string(),
        name: z.string().optional(),
        type: z.string().optional(),
        description: z.string().optional(),
    })).optional(),
    pinnedProducts: z.array(ProductSchema).optional(),
    boostedProducts: z.array(z.string()).optional(),
    buriedProducts: z.array(z.string()).optional(),
    hiddenProducts: z.array(z.string()).optional(),
    /** Catch-all: array of objects with any structure */
    customContent: z.array(z.record(z.string(), z.unknown())).optional(),
});
// SpellCheck schema
export const SpellCheckSchema = z.object({
    original: z.string(),
    corrected: z.string(),
    suggestions: z.array(z.string()),
});
// SearchMeta schema
export const SearchMetaSchema = z.object({
    totalResults: z.number(),
    totalPages: z.number(),
    currentPage: z.number(),
    pageSize: z.number(),
    suggestions: z.array(SuggestionSchema).optional(),
    // Analytics data
    analytics: SearchAnalyticsSchema.optional(),
    // Merchandising information
    merchandisingApplied: z.array(MerchandisingAppliedSchema).optional(),
    // Search corrections
    spellCheck: SpellCheckSchema.optional(),
    // Performance metrics
    queryTime: z.number().optional(),
    fromCache: z.boolean().optional(),
    // Enhanced pagination (for cursor-based systems like Shopify)
    hasNextPage: z.boolean().optional(),
    hasPreviousPage: z.boolean().optional(),
    startCursor: z.string().optional(),
    endCursor: z.string().optional(),
    // Style Matches recommendation build progress (0-100). Set when the Peacock API
    // returns `{ state: "building", percent_complete }` instead of the standard
    // Fredhopper response. UI uses < 100 to show the loading description.
    styleMatchesPercentComplete: z.number().optional(),
    // Redirect targets — present when the search response identifies a single
    // matching collection / page / product. Consumer navigates to its handle
    // instead of rendering search results.
    collection: z.object({
        id: z.number().optional(),
        title: z.string().optional(),
        handle: z.string().nullable().optional(),
    }).passthrough().optional(),
    page: z.object({
        id: z.number().optional(),
        title: z.string().optional(),
        handle: z.string().nullable().optional(),
    }).passthrough().optional(),
    product: z.object({
        id: z.number().optional(),
        title: z.string().optional(),
        handle: z.string().nullable().optional(),
    }).passthrough().optional(),
});
// SearchResponse schema
export const SearchResponseSchema = z.object({
    products: z.array(ProductSchema),
    filters: z.array(BaseFilterSchema),
    meta: SearchMetaSchema,
    sorts: z.array(SortOptionSchema),
});
// Compile-time checks
const _metaCheck = {};
const _metaCheck2 = {};
const _responseCheck = {};
const _responseCheck2 = {};
